import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Badge, Button, Card, CardBody, Input, Label, FormGroup,
  Modal, ModalBody, ModalFooter, ModalHeader, Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import {
  createInspection, getInspectionsByProperty, addInspectionMedia,
  removeInspectionMedia, closeInspection, reopenInspection, deleteInspection,
  addInspectionMessage, onInspectionMessages,
} from "../../helpers/firestoreService";
import { uploadInspectionMedia, deleteInspectionMedia, getInspectionMediaUrl } from "../../helpers/storageService";

const isVideo = (m) => (m.type || "").startsWith("video");

/**
 * Inspections for one property, or for a whole portfolio when a `properties`
 * array is passed. The owner (canManage) creates and closes them; every
 * participant can attach photos/videos and join the conversation. All of that
 * happens inside the detail modal so the section itself stays compact.
 *
 * Each entry of `properties` may carry its own `participantEmails`.
 */
const InspectionsPanel = ({
  propertyId,
  propertyAddress = "",
  properties,
  participantEmails = [],
  user,
  canManage = false,
}) => {
  const scope = useMemo(() => {
    if (properties?.length) return properties;
    return propertyId ? [{ id: propertyId, address: propertyAddress, participantEmails }] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, propertyId, propertyAddress]);
  const multi = !!properties;

  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ title: "", scheduledDate: "", notes: "", propertyId: "" });
  const [saving, setSaving] = useState(false);

  const [active, setActive] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [mediaUrls, setMediaUrls] = useState({});
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    if (scope.length === 0) { setInspections([]); setLoading(false); return; }
    setLoading(true);
    try {
      const perProperty = await Promise.all(
        scope.map((p) =>
          getInspectionsByProperty(p.id)
            .then((rows) => rows.map((r) => ({ ...r, property: p })))
            .catch(() => [])
        )
      );
      setInspections(
        perProperty.flat().sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
      );
    } catch (e) {
      console.error("Load inspections error:", e);
      setError("Couldn't load inspections.");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!active?.id) { setMessages([]); return; }
    const unsubscribe = onInspectionMessages(active.id, setMessages);
    return () => unsubscribe();
  }, [active?.id]);

  // Media keys are S3 keys — resolve fresh presigned GET URLs whenever the
  // open inspection's media list changes (upload/delete).
  useEffect(() => {
    const media = active?.media || [];
    if (media.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        media.map(async (m) => {
          try {
            return [m.storagePath, await getInspectionMediaUrl(active.id, m.storagePath)];
          } catch {
            return [m.storagePath, null];
          }
        })
      );
      if (!cancelled) setMediaUrls(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [active]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  const handleCreate = async () => {
    if (!form.title.trim()) { setError("Give the inspection a name."); return; }
    const target = scope.find((p) => p.id === form.propertyId) || scope[0];
    if (!target) { setError("Pick a property first."); return; }
    setSaving(true);
    setError("");
    try {
      await createInspection({
        propertyId: target.id,
        propertyAddress: target.address || "",
        title: form.title.trim(),
        notes: form.notes.trim(),
        scheduledDate: form.scheduledDate,
        createdBy: user.uid,
        createdByEmail: user.email,
        participantEmails: target.participantEmails || participantEmails,
      });
      setCreateModal(false);
      setForm({ title: "", scheduledDate: "", notes: "", propertyId: "" });
      await load();
    } catch (e) {
      console.error("Create inspection error:", e);
      setError("Couldn't create the inspection.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !active) return;
    setUploading(true);
    setDetailError("");
    try {
      const added = [];
      for (const file of files) {
        const uploaded = await uploadInspectionMedia(active.id, file);
        const media = {
          ...uploaded,
          uploadedBy: user.uid,
          uploadedByEmail: user.email || "",
          uploadedAt: new Date().toISOString(),
        };
        await addInspectionMedia(active.id, media);
        added.push(media);
      }
      const next = { ...active, media: [...(active.media || []), ...added] };
      setActive(next);
      setInspections((prev) => prev.map((i) => (i.id === next.id ? next : i)));
    } catch (err) {
      console.error("Upload inspection media error:", err);
      setDetailError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveMedia = async (media) => {
    if (!active) return;
    setDetailError("");
    try {
      await deleteInspectionMedia(active.id, media.storagePath);
      await removeInspectionMedia(active.id, media);
      const next = {
        ...active,
        media: (active.media || []).filter((m) => m.storagePath !== media.storagePath),
      };
      setActive(next);
      setInspections((prev) => prev.map((i) => (i.id === next.id ? next : i)));
    } catch (err) {
      console.error("Remove inspection media error:", err);
      setDetailError("Couldn't remove that file.");
    }
  };

  const handleSend = async () => {
    const body = text.trim();
    if (!body || !active) return;
    setSending(true);
    try {
      await addInspectionMessage(active.id, {
        senderUid: user.uid,
        senderName: user.displayName || user.email || "",
        senderEmail: user.email || "",
        text: body,
      });
      setText("");
    } catch (err) {
      console.error("Inspection message error:", err);
      setDetailError("Message couldn't be sent.");
    } finally {
      setSending(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!active) return;
    const closing = active.status !== "closed";
    try {
      if (closing) await closeInspection(active.id);
      else await reopenInspection(active.id);
      const next = { ...active, status: closing ? "closed" : "open" };
      setActive(next);
      setInspections((prev) => prev.map((i) => (i.id === next.id ? next : i)));
    } catch (err) {
      console.error("Update inspection status error:", err);
      setDetailError("Couldn't update the status.");
    }
  };

  const handleDelete = async () => {
    if (!active || !window.confirm("Delete this inspection and its conversation?")) return;
    try {
      await deleteInspection(active.id);
      setInspections((prev) => prev.filter((i) => i.id !== active.id));
      setActive(null);
    } catch (err) {
      console.error("Delete inspection error:", err);
      setDetailError("Couldn't delete the inspection.");
    }
  };

  const isClosed = active?.status === "closed";

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            <i className="bx bx-clipboard text-info me-2" />
            Inspections
          </h5>
          {canManage && (
            <Button
              color="info"
              size="sm"
              disabled={scope.length === 0}
              onClick={() => {
                setError("");
                setForm({ title: "", scheduledDate: "", notes: "", propertyId: scope[0]?.id || "" });
                setCreateModal(true);
              }}
            >
              <i className="bx bx-plus me-1" /> New Inspection
            </Button>
          )}
        </div>

        {error && <Alert color="danger" className="py-2 small">{error}</Alert>}

        {loading ? (
          <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
        ) : inspections.length === 0 ? (
          <p className="text-muted small fst-italic mb-0">
            No inspections yet.{canManage ? " Create one to start collecting photos and notes." : ""}
          </p>
        ) : (
          <div className="list-group list-group-flush">
            {inspections.map((i) => (
              <button
                key={i.id}
                type="button"
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center px-0"
                onClick={() => { setDetailError(""); setActive(i); }}
              >
                <span className="text-truncate">
                  <span className="fw-semibold">{i.title}</span>
                  {i.scheduledDate && <small className="text-muted ms-2">{i.scheduledDate}</small>}
                  {multi && (
                    <small className="text-muted d-block">
                      <i className="bx bx-building-house me-1" />
                      {i.property?.address || i.propertyAddress || i.propertyId}
                    </small>
                  )}
                </span>
                <span className="d-flex align-items-center gap-2 flex-shrink-0">
                  {(i.media || []).length > 0 && (
                    <small className="text-muted">
                      <i className="bx bx-image me-1" />{(i.media || []).length}
                    </small>
                  )}
                  <Badge color={i.status === "closed" ? "secondary" : "success"} pill>
                    {i.status === "closed" ? "Closed" : "Open"}
                  </Badge>
                </span>
              </button>
            ))}
          </div>
        )}
      </CardBody>

      {/* New inspection */}
      <Modal isOpen={createModal} toggle={() => setCreateModal(false)}>
        <ModalHeader toggle={() => setCreateModal(false)}>New Inspection</ModalHeader>
        <ModalBody>
          {multi && (
            <FormGroup>
              <Label className="small fw-semibold">Property *</Label>
              <Input
                type="select"
                bsSize="sm"
                value={form.propertyId}
                onChange={(e) => setForm({ ...form, propertyId: e.target.value })}
              >
                {scope.map((p) => (
                  <option key={p.id} value={p.id}>{p.address}</option>
                ))}
              </Input>
            </FormGroup>
          )}
          <FormGroup>
            <Label className="small fw-semibold">Name *</Label>
            <Input
              bsSize="sm"
              placeholder="e.g. Move-in inspection"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label className="small fw-semibold">Date</Label>
            <Input
              type="date"
              bsSize="sm"
              value={form.scheduledDate}
              onChange={(e) => setForm({ ...form, scheduledDate: e.target.value })}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="small fw-semibold">Notes</Label>
            <Input
              type="textarea"
              rows={3}
              bsSize="sm"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCreateModal(false)}>Cancel</Button>
          <Button color="info" onClick={handleCreate} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Create"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Inspection detail: media + conversation */}
      <Modal isOpen={!!active} toggle={() => setActive(null)} size="lg" scrollable>
        <ModalHeader toggle={() => setActive(null)}>
          {active?.title}
          <Badge color={isClosed ? "secondary" : "success"} pill className="ms-2 fw-normal">
            {isClosed ? "Closed" : "Open"}
          </Badge>
        </ModalHeader>
        <ModalBody>
          {detailError && <Alert color="danger" className="py-2 small">{detailError}</Alert>}
          {multi && active?.propertyId && (
            <p className="small mb-1">
              <Link to={`/properties/${active.propertyId}`} className="text-decoration-none">
                <i className="bx bx-building-house me-1" />
                {active.property?.address || active.propertyAddress || active.propertyId}
              </Link>
            </p>
          )}
          {active?.scheduledDate && (
            <p className="text-muted small mb-1"><i className="bx bx-calendar me-1" />{active.scheduledDate}</p>
          )}
          {active?.notes && <p className="small mb-3" style={{ whiteSpace: "pre-wrap" }}>{active.notes}</p>}

          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted text-uppercase fw-semibold" style={{ fontSize: "0.68rem", letterSpacing: "0.05em" }}>
              Photos &amp; Videos
            </small>
            {!isClosed && (
              <Label
                className="btn btn-outline-info btn-sm mb-0"
                htmlFor="inspection-media-upload"
                style={{ cursor: uploading ? "wait" : "pointer" }}
              >
                {uploading ? <Spinner size="sm" /> : <><i className="bx bx-upload me-1" />Add</>}
              </Label>
            )}
            <input
              id="inspection-media-upload"
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={handleUpload}
            />
          </div>

          {!isClosed && (
            <p className="text-muted mb-2" style={{ fontSize: "0.7rem" }}>
              By uploading, you agree to our{" "}
              <a href="/community-guidelines" target="_blank" rel="noopener noreferrer">Community Guidelines</a>{" "}
              and confirm you have the right to share this content. See our{" "}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> for how photos and videos are handled.
            </p>
          )}

          {(active?.media || []).length === 0 ? (
            <p className="text-muted small fst-italic">No photos or videos yet.</p>
          ) : (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {(active?.media || []).map((m) => {
                const mine = m.uploadedBy === user?.uid;
                const resolvedUrl = mediaUrls[m.storagePath];
                return (
                  <div key={m.storagePath} className="position-relative border rounded overflow-hidden" style={{ width: 110 }}>
                    {!resolvedUrl ? (
                      <div className="d-flex align-items-center justify-content-center" style={{ width: "100%", height: 80, background: "#f1f1f5" }}>
                        <Spinner size="sm" color="secondary" />
                      </div>
                    ) : isVideo(m) ? (
                      <video src={resolvedUrl} controls style={{ width: "100%", height: 80, objectFit: "cover", background: "#000" }} />
                    ) : (
                      <a href={resolvedUrl} target="_blank" rel="noreferrer">
                        <img src={resolvedUrl} alt={m.name} style={{ width: "100%", height: 80, objectFit: "cover" }} />
                      </a>
                    )}
                    {!isClosed && (mine || canManage) && (
                      <Button
                        color="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 p-0 px-1 lh-1"
                        title="Remove"
                        onClick={() => handleRemoveMedia(m)}
                      >
                        <i className="bx bx-x" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <small className="text-muted text-uppercase fw-semibold d-block mb-2" style={{ fontSize: "0.68rem", letterSpacing: "0.05em" }}>
            Conversation
          </small>
          <div ref={boxRef} className="border rounded p-2 overflow-auto" style={{ height: 200, background: "#fbfbfd" }}>
            {messages.length === 0 ? (
              <p className="text-muted small fst-italic text-center my-4 mb-0">No messages yet.</p>
            ) : (
              messages.map((m) => {
                const isSelf = m.senderUid === user?.uid;
                return (
                  <div key={m.id} className={`d-flex mb-2 ${isSelf ? "justify-content-end" : "justify-content-start"}`}>
                    <div
                      className={`rounded px-3 py-2 ${isSelf ? "bg-primary text-white" : "bg-white border"}`}
                      style={{ maxWidth: "80%", fontSize: "0.85rem" }}
                    >
                      {!isSelf && (
                        <small className="d-block fw-semibold text-primary mb-1">{m.senderName || m.senderEmail}</small>
                      )}
                      <span style={{ whiteSpace: "pre-wrap" }}>{m.text}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {!isClosed && (
            <div className="d-flex gap-2 mt-2">
              <Input
                type="textarea"
                rows={1}
                bsSize="sm"
                placeholder="Write a message…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
                style={{ resize: "none" }}
              />
              <Button color="primary" size="sm" disabled={!text.trim() || sending} onClick={handleSend}>
                {sending ? <Spinner size="sm" /> : <i className="bx bx-send" />}
              </Button>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <div>
            {canManage && (
              <Button color="danger" outline size="sm" onClick={handleDelete}>
                <i className="bx bx-trash me-1" />Delete
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            {canManage && (
              <Button color={isClosed ? "success" : "warning"} size="sm" onClick={handleToggleStatus}>
                {isClosed ? "Reopen" : "Close Inspection"}
              </Button>
            )}
            <Button color="secondary" size="sm" onClick={() => setActive(null)}>Close</Button>
          </div>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

export default InspectionsPanel;
